interface AspectRatioPreviewProps {
  aspectRatio: string
  className?: string
  maxWidth?: number
  maxHeight?: number
}

function parseRatio(aspectRatio: string): [number, number] {
  const parts = aspectRatio.split(':').map(Number)
  if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) {
    return [parts[0], parts[1]]
  }
  return [1, 1]
}

function getDisplayDimensions(
  w: number,
  h: number,
  maxW: number,
  maxH: number,
): { width: number; height: number } {
  const ratioW = maxW / w
  const ratioH = maxH / h
  const scale = Math.min(ratioW, ratioH)
  return {
    width: Math.round(w * scale),
    height: Math.round(h * scale),
  }
}

export function AspectRatioPreview({
  aspectRatio,
  className = '',
  maxWidth = 160,
  maxHeight = 120,
}: AspectRatioPreviewProps) {
  const [w, h] = parseRatio(aspectRatio)
  const { width, height } = getDisplayDimensions(w, h, maxWidth, maxHeight)
  const patternId = `checker-${aspectRatio.replace(':', 'x')}`

  return (
    <div className={`flex flex-col items-center gap-1.5 ${className}`}>
      <div
        style={{ width: maxWidth, height: maxHeight }}
        className="flex items-center justify-center"
      >
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          xmlns="http://www.w3.org/2000/svg"
          style={{ transition: 'width 0.2s ease, height 0.2s ease' }}
          aria-label={`Canvas preview ${aspectRatio}`}
        >
          <defs>
            <pattern
              id={patternId}
              width="8"
              height="8"
              patternUnits="userSpaceOnUse"
            >
              <rect width="8" height="8" fill="#f8fafc" />
              <rect width="4" height="4" fill="#e2e8f0" />
              <rect x="4" y="4" width="4" height="4" fill="#e2e8f0" />
            </pattern>
          </defs>
          <rect
            width={width}
            height={height}
            fill={`url(#${patternId})`}
            rx="3"
            ry="3"
          />
          <rect
            width={width}
            height={height}
            fill="none"
            stroke="#94a3b8"
            strokeWidth="1.5"
            rx="3"
            ry="3"
          />
          <text
            x={width / 2}
            y={height / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={Math.min(width, height) * 0.18}
            fill="#94a3b8"
            fontFamily="system-ui, sans-serif"
            fontWeight="500"
          >
            {aspectRatio}
          </text>
        </svg>
      </div>
      <span className="text-[11px] text-slate-400 font-medium tracking-wide uppercase">
        {aspectRatio}
        {w !== h ? (w > h ? ' · Landscape' : ' · Portrait') : ' · Square'}
      </span>
    </div>
  )
}
